import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { PermissionSlug } from '../../constants/featureFlags';
import type { VisitTaskDetailFields } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { TaskDetailsDialog } from './TaskDetailsDialog';
import type { TaskHeaderSecondaryAction } from './TaskHeaderControls';
import { useDeferredDialogOpener } from './useDeferredDialogOpener';

interface UseTaskDetailsActionArgs {
  /** The task on screen. No action is offered until there is one. */
  taskId: string | null | undefined;
  patientId?: string;
  targetLabel?: string;
  /** A deleted task has no details to open. */
  isDeleted?: boolean;
  onNotesSaved?: (notes: string) => void;
  onDocumentsChanged?: () => void;
  /** A visit's location of care, HCPCS code or mileage was saved. */
  onVisitFieldsSaved?: (
    fields: VisitTaskDetailFields,
    written: readonly string[]
  ) => void;
}

interface TaskDetailsAction {
  /** The Actions-menu entry, or none for a user who cannot view Task Details. */
  actions: TaskHeaderSecondaryAction[];
  /** Render once, anywhere in the panel. */
  dialog: ReactNode;
}

/**
 * The "Task Details" entry every task panel puts in its Actions menu, and the
 * dialog it opens. One hook so the four panels (visit, order, communication,
 * misc task) cannot drift on who sees the entry or what it opens.
 */
export function useTaskDetailsAction({
  taskId,
  patientId,
  targetLabel,
  isDeleted = false,
  onNotesSaved,
  onDocumentsChanged,
  onVisitFieldsSaved,
}: UseTaskDetailsActionArgs): TaskDetailsAction {
  const canView = useStore(state =>
    state.hasPermission(PermissionSlug.TASK_DETAILS_VIEW)
  );
  const canEdit = useStore(state =>
    state.hasPermission(PermissionSlug.TASK_DETAILS_EDIT)
  );
  const openDeferred = useDeferredDialogOpener();
  const [open, setOpen] = useState(false);

  const offered = Boolean(taskId) && canView && !isDeleted;
  // Stable while nothing changes, so a panel can list it as a memo dependency.
  const actions = useMemo<TaskHeaderSecondaryAction[]>(
    () =>
      offered
        ? [
            {
              label: 'Task Details',
              icon: <Info className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () => openDeferred('taskDetails', () => setOpen(true)),
            },
          ]
        : [],
    [offered, openDeferred]
  );

  // Taking the action away closes the dialog for good. Without this it would
  // reopen by itself if the action came back, as it does when a permission is
  // lost and regained on a later refresh of the signed-in user.
  useEffect(() => {
    if (!offered) setOpen(false);
  }, [offered]);

  // Mounted only while open: a closed dialog has nothing to load or watch.
  if (!taskId || !offered || !open) {
    return { actions, dialog: null };
  }

  return {
    actions,
    dialog: (
      <TaskDetailsDialog
        open={open}
        onOpenChange={setOpen}
        taskId={taskId}
        patientId={patientId}
        targetLabel={targetLabel}
        canEdit={canEdit}
        onNotesSaved={onNotesSaved}
        onDocumentsChanged={onDocumentsChanged}
        onVisitFieldsSaved={onVisitFieldsSaved}
      />
    ),
  };
}
